import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  IconButton, 
  Avatar,
  Divider,
  InputBase,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Button,
  Snackbar,
  Alert
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import AuthContext from "flexbiz-core/components/auth/Context";

// Import components cho Tree View từ @mui/x-tree-view
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';

// Icons cho Node của Tree
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import Container from 'flexbiz-core/components/AppContainer';

export default function NetworkPage() {
  const history = useHistory();
  const context = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [networkData, setNetworkData] = useState({
    referral_code: '',
    collaborators: []
  });
  const [searchTerm, setSearchTerm] = useState('');

  // --- STATE CHO THÔNG BÁO COPY ---
  const [toastOpen, setToastOpen] = useState(false);

  // ================= GỌI API LẤY DANH SÁCH MẠNG LƯỚI =================
  useEffect(() => {
    const fetchNetworkData = async () => {
      setLoading(true);
      try {
        const response = await context.apis.asyncGetReport(
          context.userInfo.token,
          "referredusers",
          { group_by: "referral_code" }
        );

        const data = response?.data || response;
        if (data) {
          setNetworkData({
            referral_code: data.referral_code || '',
            collaborators: data.collaborators || []
          });
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu mạng lưới CTV:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNetworkData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ================= NỘI DUNG CHIA SẺ (COPYWRITING) =================
  let domain = (context.config?.home_url||"").split("#")[0];
  if (!domain) {
    domain = window.location.origin;
  }
  if (domain.endsWith('/')) {
    domain = domain.slice(0, -1);
  }

  const referral_page =  `${domain}/#/referral?code=${networkData.referral_code}`;
  const shareTitle = "Cơ hội gia tăng thu nhập thụ động cùng ZenReal! 🚀";
  const shareText = `Chào bạn, mình đang tham gia mạng lưới phân phối BĐS công nghệ mới với chính sách hoa hồng cực kỳ hấp dẫn. Không gò bó thời gian, không ép doanh số! \n\n👉 Trở thành đối tác ngay hôm nay để nhận hoa hồng trực tiếp và gián tiếp không giới hạn. \n🌐 Tải app và đăng ký tại: ${referral_page}`;

  // ================= HANDLERS: COPY & SHARE =================
  const handleCopyCode = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(networkData.referral_code)
        .then(() => setToastOpen(true))
        .catch(err => console.error('Lỗi copy:', err));
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = networkData.referral_code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setToastOpen(true);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
        });
      } catch (error) {
        console.log('Hủy chia sẻ hoặc có lỗi:', error);
      }
    } else {
      navigator.clipboard.writeText(shareText);
      context.alert("Đã copy toàn bộ nội dung lời mời. Bạn có thể dán vào Zalo/Facebook để gửi!");
    }
  };

  // ================= UTILS =================
  const getInitials = (name) => {
    if (!name) return <PersonIcon />;
    const parts = name.trim().split(' ');
    return parts[parts.length - 1].charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return '#9e9e9e';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return `hsl(${hash % 360}, 70%, 40%)`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return '---';
    const date = new Date(isoString);
    return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // ================= DATA LOGIC: FLAT TO TREE =================
  const totalCollaborators = networkData.collaborators.length;
  const totalNetworkSales = networkData.collaborators.reduce((sum, ctv) => sum + (ctv.bds_sales || 0), 0);

  // 1. Lọc dữ liệu trước (nếu người dùng gõ tìm kiếm)
  const filteredCollaborators = networkData.collaborators.filter(ctv => {
    const term = searchTerm.toLowerCase();
    const nameMatch = (ctv.name || '').toLowerCase().includes(term);
    const userMatch = (ctv.user || '').toLowerCase().includes(term);
    return nameMatch || userMatch;
  });

  // 2. Chuyển đổi dữ liệu phẳng (Flat) thành dạng Cây (Tree)
  const buildTree = (flatData) => {
    const map = {};
    const roots = [];

    // Khởi tạo map chứa tất cả các node
    flatData.forEach(node => {
      map[node.user] = { ...node, children: [] };
    });

    // Móc nối quan hệ cha - con
    flatData.forEach(node => {
      // Nếu có field 'by' và người giới thiệu đó có tồn tại trong danh sách
      if (node.by && map[node.by]) {
        map[node.by].children.push(map[node.user]);
      } else {
        // Nếu không có 'by', hoặc 'by' không nằm trong list này, nó là Root Node
        roots.push(map[node.user]);
      }
    });

    return roots;
  };

  const treeData = buildTree(filteredCollaborators);

  // 3. Hàm đệ quy để vẽ các phần tử TreeItem
  const renderTree = (nodes) => (
    nodes.map((node) => (
      <TreeItem 
        key={node.user} 
        itemId={node.user} 
        label={
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            py: 1.5,
            borderBottom: '1px solid #f2f2f7'
          }}>
            {/* Cột trái: Avatar + Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, overflow: 'hidden' }}>
              <Avatar 
                sx={{ 
                  bgcolor: getAvatarColor(node.name || node.user), 
                  width: 44, 
                  height: 44, 
                  mr: 1.5, 
                  fontWeight: 'bold',
                  fontSize: '1.1rem'
                }}
              >
                {getInitials(node.name || node.user)}
              </Avatar>
              <Box sx={{ overflow: 'hidden', pr: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold" noWrap>
                  {node.name || 'Thành viên mới'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  ID: {node.user}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2, color: 'text.secondary' }}>
                  <CalendarTodayIcon sx={{ fontSize: 12, mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    {formatDate(node.date)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Cột phải: Level & Doanh số */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 60 }}>
              <Chip 
                label={`Level ${node.level || 1}`} 
                size="small" 
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold', fontSize: '0.65rem', mb: 0.5, height: 20 }} 
              />
              <Typography variant="subtitle1" fontWeight="bold" color="#388e3c" sx={{ lineHeight: 1 }}>
                {node.bds_sales || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                giao dịch
              </Typography>
            </Box>
          </Box>
        }
        sx={{
          '& .MuiTreeItem-content': {
            p: 0, // Xóa padding mặc định
            bgcolor: 'transparent !important', // Xóa màu nền khi click/hover để giống danh sách bình thường
          },
          '& .MuiTreeItem-iconContainer': {
            width: 24, // Giới hạn kích thước vùng bấm icon mở rộng
          }
        }}
      >
        {/* Đệ quy gọi lại chính nó nếu có children */}
        {Array.isArray(node.children) && node.children.length > 0 
          ? renderTree(node.children) 
          : null}
      </TreeItem>
    ))
  );
  return (
    <Container title="Mạng lưới CTV" onBack={() => history.goBack()}>
      <Box sx={{p: 2, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* THẺ TỔNG QUAN WIDGET */}
            <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 8px 16px rgba(30, 60, 114, 0.2)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Tổng quan đội nhóm
                  </Typography>
                  <Chip 
                    icon={<QrCodeIcon fontSize="small" sx={{ color: 'white !important' }} />} 
                    label={`Mã: ${networkData.referral_code || '---'}`} 
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
                  />
                </Box>
                
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 2 }} />

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <GroupIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Tổng thành viên</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold">
                      {totalCollaborators} <Typography component="span" variant="body2">người</Typography>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <TrendingUpIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>Giao dịch tuyến dưới</Typography>
                    </Box>
                    <Typography variant="h5" fontWeight="bold">
                      {totalNetworkSales} <Typography component="span" variant="body2">đơn BĐS</Typography>
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={handleCopyCode}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }, textTransform: 'none', borderRadius: 2 }}
                  >
                    Copy Mã
                  </Button>
                  <Button 
                    variant="contained" 
                    fullWidth
                    startIcon={<ShareIcon fontSize="small" />}
                    onClick={handleShare}
                    sx={{ bgcolor: '#ff9800', color: 'white', '&:hover': { bgcolor: '#f57c00' }, textTransform: 'none', borderRadius: 2, fontWeight: 'bold' }}
                  >
                    Mời gọi CTV
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* THANH TÌM KIẾM NỘI BỘ */}
            <Paper elevation={0} sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: '100%', mb: 2, borderRadius: 3, border: '1px solid #e0e0e0' }}>
              <IconButton sx={{ p: '10px' }} aria-label="search" disabled>
                <SearchIcon color="action" />
              </IconButton>
              <InputBase
                sx={{ ml: 1, flex: 1, fontSize: '0.95rem' }}
                placeholder="Tìm CTV theo tên hoặc ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Paper>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Danh sách cộng tác viên ({filteredCollaborators.length})
            </Typography>

            {/* DANH SÁCH TREE VIEW */}
            {treeData.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  Không tìm thấy cộng tác viên nào.
                </Typography>
              </Box>
            ) : (
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <CardContent sx={{ p: '8px 16px !important' }}>
                  <SimpleTreeView
                    slots={{ collapseIcon: ExpandMoreIcon, expandIcon: ChevronRightIcon }}
                    sx={{ width: '100%', overflowX: 'hidden' }}
                  >
                    {renderTree(treeData)}
                  </SimpleTreeView>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>

      {/* SNACKBAR THÔNG BÁO COPY */}
      <Snackbar open={toastOpen} autoHideDuration={2000} onClose={() => setToastOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
          Đã copy mã giới thiệu!
        </Alert>
      </Snackbar>
    </Container>
  );
}